/**
 * L'écran unique des factures.
 *
 * ## Ce qu'il remplace, et pourquoi il n'enlève rien
 *
 * `/admin/payments` (808 lignes) et `/admin/credit-notes` (623 lignes) lisaient
 * **les quatre mêmes tables**, avec la même recherche et le même tableau.
 * C'étaient déjà des quasi-jumeaux : la fusion n'enlève pas une fonction, elle
 * enlève un doublon.
 *
 * Mais les deux affichaient `invoices.status`. Mesuré le 10 septembre 2026, la
 * colonne stockée et le solde calculé se contredisent sur **2 116 factures sur
 * 3 711 — 57 %** :
 *
 * | la colonne disait | la réalité | factures |
 * | --- | --- | ---: |
 * | « en attente » | soldée | 2 020 |
 * | « payée » | **encore due** | 44 |
 * | « en attente » | partiellement payée | 48 |
 *
 * Cet écran lit `v_admin_factures`, qui calcule le solde depuis les
 * affectations à chaque lecture. Il n'a **aucun accès** à la colonne stockée :
 * la vue ne l'expose pas.
 *
 * ## Le bouton qui n'est pas là
 *
 * `/admin/payments` portait « Réconcilier tout », qui appelle
 * `reconcile_all_pending_invoices`. Cette fonction boucle sur les factures
 * `pending` et recalcule leur statut en lisant… `bank_transactions`, table
 * sans une seule écriture depuis le 27 juillet 2025. **Elle ne peut rien
 * corriger** : elle relit une table gelée depuis treize mois.
 *
 * Le bouton n'est pas repris. Un bouton qui promet une réparation qu'il ne
 * fait pas est pire qu'un bouton absent : on l'actionne, on voit « 2 020
 * factures traitées », et on en conclut que c'est réglé.
 *
 * ## Ce qu'il ne cache pas, et ce qu'il a cessé de confondre
 *
 * Une facture annulée dont le solde reste inscrit demeure visible : le §5.6 de
 * la passation interdit de « faire disparaître la contradiction par un
 * filtre ».
 *
 * La première version la montrait mal. Elle marquait « réclame encore » sur
 * onze factures d'un seul mot, alors qu'il y a **deux populations** que rien
 * ne relie — signalé en production sur `CDV-TEST-2602043` :
 *
 * | | factures | résiduel | ce qui le résout |
 * | --- | ---: | ---: | --- |
 * | annulées, document parti | **10** | **1 093,00 EUR** | une note de crédit |
 * | annulées avant tout envoi | **1** | **1,50 EUR** | rien |
 *
 * Les dix sont de juin 2025 : la famille a reçu une facture, il faut une pièce
 * comptable pour l'effacer. La onzième est un panier abandonné annulé par
 * `cancel_order` — aucun document n'est jamais sorti, donc l'annulation se
 * suffit à elle-même.
 *
 * Et **aucune des deux ne réclame quoi que ce soit à une famille.** « Reste
 * dû » ne compte donc plus que ce qu'une famille doit vraiment, ici comme dans
 * le total de la page et dans le filtre. Le résiduel reste affiché, en gris,
 * nommé pour ce qu'il est.
 *
 * ## Le bouton de note de crédit dit pourquoi il ne peut pas
 *
 * `create_credit_note` refuse **toute** facture déjà annulée :
 *
 * ```sql
 * IF v_invoice.status = 'cancelled' THEN
 *   RAISE EXCEPTION 'la facture % est deja annulee', v_invoice.invoice_number
 * ```
 *
 * La première version peignait le bouton en gris sans le désactiver : il avait
 * l'air interdit, ne disait pas pourquoi, et menait à une exception SQL brute
 * si on insistait. Il est maintenant réellement désactivé, et son titre nomme
 * le refus.
 *
 * Conséquence à ne pas perdre de vue : ce refus bloque la reprise des dix
 * factures de juin 2025. La règle D1 fonctionne vers l'avant — la note précède
 * l'annulation, et le déclencheur passe la facture à `cancelled` — mais le
 * stock déjà annulé ne peut pas être crédité en l'état.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Download,
  FileText, Loader2, PiggyBank, Receipt, RefreshCw, Search, Send, X,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import clsx from 'clsx';
import CreditNoteModal from './components/CreditNoteModal';
import DetailFacture from './components/DetailFacture';
import FileProvisions from './components/FileProvisions';
import { dateCourte, euros } from './components/money';
import { messageErreur } from '../../lib/payments/erreur';
import { produireEtEnvoyerFacture } from '../../lib/orders/gateway.supabase';
import {
  chargerFactures, chargerNotesDeCredit, TAILLE_DE_PAGE,
} from '../../lib/factures/gateway.supabase';
import { useSearchParams } from 'react-router-dom';
import { lireRecherche } from '../../lib/factures/recherche';
import {
  ETATS, FILTRES, RECLAMATIONS, TRIS, empechementNoteDeCredit, etiquetteFamille,
  reclamation, type Filtre, type LigneFacture, type NoteDeCredit, type Tri,
} from '../../lib/factures/types';

type Onglet = 'factures' | 'provisions';

export default function AdminFacturesPage() {
  const [onglet, setOnglet] = useState<Onglet>('factures');

  const [lignes, setLignes] = useState<LigneFacture[]>([]);
  const [notes, setNotes] = useState<Map<string, NoteDeCredit[]>>(new Map());
  const [total, setTotal] = useState<number | null>(null);
  const [centimesAffiches, setCentimesAffiches] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [tri, setTri] = useState<Tri>('recentes');
  /*
    Deux états pour la recherche : ce qui est TAPÉ, et ce qui est CHERCHÉ.
    Sans cette séparation, chaque frappe partirait en requête sur 3 711
    factures. L'envoi se fait au `submit` — une recherche est un geste, pas un
    effet de bord de la frappe.
  */
  /*
    `?q=` dans l'adresse : c'est par là que le contrôle du compte client amène
    ici avec un numéro de facture déjà cherché. Sans ce paramètre, il faudrait
    recopier le numéro à la main — et une décision qu'on prend à la main en
    recopiant se prend une fois sur deux.

    Lu UNE fois, à la construction. En faire un état dérivé à chaque rendu
    rendrait la barre de recherche inutilisable : chaque frappe se ferait
    écraser par l'adresse.
  */
  const [parametres, setParametres] = useSearchParams();
  const [saisie, setSaisie] = useState(() => parametres.get('q') ?? '');
  const [recherche, setRecherche] = useState(() => parametres.get('q') ?? '');
  const champDeRecherche = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(0);

  const [deplie, setDeplie] = useState<string | null>(null);
  const [invoiceACrediter, setInvoiceACrediter] = useState<string | null>(null);
  const [envoiEnCours, setEnvoiEnCours] = useState<string | null>(null);

  /* Ce que la règle a compris de la saisie, pour le dire sous la barre. */
  const comprise = useMemo(() => lireRecherche(saisie), [saisie]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await chargerFactures({
        recherche, filtre, tri,
        taille: TAILLE_DE_PAGE,
        depuis: page * TAILLE_DE_PAGE,
      });
      setLignes(r.lignes);
      setTotal(r.total);
      setCentimesAffiches(r.centimesAffiches);
      /*
        Les notes de crédit de la PAGE seulement. Les lire pour les 3 711
        factures dépasserait le plafond de mille lignes de PostgREST, qui
        tronque en silence — ce n'est pas une erreur, c'est une réponse courte.
      */
      setNotes(await chargerNotesDeCredit(r.lignes.map((l) => l.invoiceId)));
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture des factures impossible'));
    } finally {
      setChargement(false);
    }
  }, [recherche, filtre, tri, page]);

  useEffect(() => { void charger(); }, [charger]);

  /* Changer un critère ramène à la première page : rester en page 4 d'un
     résultat qui n'en a qu'une donne un écran vide sans explication. */
  const changerFiltre = (f: Filtre) => { setFiltre(f); setPage(0); setDeplie(null); };
  const changerTri = (t: Tri) => { setTri(t); setPage(0); setDeplie(null); };
  const lancerRecherche = (texte: string) => {
    setRecherche(texte);
    setPage(0);
    setDeplie(null);
    /* L'adresse suit la recherche : un écran trouvé se partage et se recharge.
       `replace` pour ne pas empiler une entrée d'historique par recherche. */
    setParametres(texte.trim() === '' ? {} : { q: texte.trim() }, { replace: true });
  };

  /*
    La croix efface la saisie ET la recherche en cours. N'effacer que le
    champ laisserait la liste filtrée sous un champ vide : l'écran montrerait
    quatre factures sans plus dire pourquoi.
  */
  const effacerRecherche = () => {
    setSaisie('');
    if (recherche !== '') lancerRecherche('');
    champDeRecherche.current?.focus();
  };

  const pages = total === null ? 1 : Math.max(1, Math.ceil(total / TAILLE_DE_PAGE));

  /*
    Produire ou renvoyer la facture.

    `forcer` : le clic est un geste délibéré. Sans lui, une facture qui porte
    déjà un PDF serait considérée comme déjà envoyée, et « renvoyer » ne
    renverrait rien.

    Le bouton ENVOIE un courriel à la famille : il demande donc confirmation.
  */
  const envoyerLaFacture = async (f: LigneFacture) => {
    if (!f.courriel) {
      toast.error("Cette famille n'a pas d'adresse courriel.");
      return;
    }
    const question = f.pdfUrl
      ? `Renvoyer la facture ${f.invoiceNumber} à ${f.courriel} ?`
      : `Produire la facture ${f.invoiceNumber} et l'envoyer à ${f.courriel} ?`;
    if (!window.confirm(question)) return;

    setEnvoiEnCours(f.invoiceNumber);
    try {
      const r = await produireEtEnvoyerFacture(f.invoiceNumber, f.courriel, { forcer: true });
      if (r.erreur) {
        toast.error(`Facture ${f.invoiceNumber} : ${r.erreur}`);
      } else {
        toast.success(`Facture ${f.invoiceNumber} envoyée à ${f.courriel}.`);
        if (r.pdfUrl) {
          setLignes((liste) => liste.map((l) =>
            l.invoiceNumber === f.invoiceNumber ? { ...l, pdfUrl: r.pdfUrl ?? l.pdfUrl } : l));
        }
      }
    } catch (e) {
      toast.error(messageErreur(e, `Envoi de ${f.invoiceNumber} impossible`));
    } finally {
      setEnvoiEnCours(null);
    }
  };

  /*
    L'export porte sur ce qui est AFFICHÉ, et le dit dans son nom de fichier.

    L'ancien écran exportait « les factures », c'est-à-dire les dix lignes de
    la page courante, sous le nom `factures_<date>.csv`. Un fichier qui
    s'appelle « factures » et n'en contient que dix laisse croire qu'il les
    contient toutes.
  */
  const exporterCsv = () => {
    const entetes = [
      'Numéro', 'Date', 'Échéance', 'Famille', 'Courriel', 'État',
      'Facturé', 'Notes de crédit', 'Net', 'Reçu', 'Reste dû', 'Trop-perçu',
      'Communication structurée', 'Document délivré',
    ];
    const cellule = (v: string | number | null) => {
      const s = v === null ? '' : String(v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lignesCsv = lignes.map((l) => [
      l.invoiceNumber,
      dateCourte(l.creeLe),
      dateCourte(l.echeanceLe),
      etiquetteFamille(l.nom, l.prenom),
      l.courriel ?? '',
      ETATS[l.settlement].label,
      (l.factureCents / 100).toFixed(2),
      (l.notesCents / 100).toFixed(2),
      (l.netCents / 100).toFixed(2),
      (l.recuCents / 100).toFixed(2),
      (l.resteCents / 100).toFixed(2),
      (l.tropPercuCents / 100).toFixed(2),
      l.communicationStructuree ?? '',
      l.documentDelivre ? 'oui' : 'non',
    ].map(cellule).join(';'));

    const contenu = [entetes.join(';'), ...lignesCsv].join('\n');
    /*
      Le BOM, en séquence d'échappement et non en caractère : sans lui, Excel
      lit « Créé » comme « CrÃ©Ã© » ; écrit littéralement, il est invisible
      dans le source et `no-irregular-whitespace` le refuse — à raison, parce
      qu'un caractère invisible dans du code est un piège.
    */
    const blob = new Blob([`\uFEFF${contenu}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factures_page-${page + 1}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${lignes.length} facture${lignes.length > 1 ? 's' : ''} exportée${lignes.length > 1 ? 's' : ''} — la page affichée`);
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Factures</h1>
          <p className="text-gray-600">
            Le solde réel de chaque facture, ses notes de crédit et ses paiements — en une page.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={exporterCsv} disabled={lignes.length === 0}
                  className="btn-outline flex items-center">
            <Download className="mr-2 h-4 w-4" />
            Exporter la page
          </button>
          <button type="button" onClick={charger} disabled={chargement}
                  className="btn-primary flex items-center">
            <RefreshCw className={clsx('mr-2 h-4 w-4', chargement && 'animate-spin')} />
            Actualiser
          </button>
        </div>
      </div>

      {/* --- Les deux onglets ------------------------------------------- */}
      <div className="flex gap-1 border-b border-gray-200">
        <Onglets courant={onglet} valeur="factures" onChange={setOnglet} icone={Receipt}>
          Factures
        </Onglets>
        <Onglets courant={onglet} valeur="provisions" onChange={setOnglet} icone={PiggyBank}>
          Provisions
        </Onglets>
      </div>

      {onglet === 'provisions' ? <FileProvisions /> : (
        <>
          {/* --- Chercher, filtrer, trier ------------------------------ */}
          {/*
            La recherche seule sur sa ligne, le filtre et le tri en dessous.

            Les deux listes portent `form-input`, donc `w-full`. Sur la même
            ligne que le champ, chacune réclamait toute la largeur, et
            flexbox répartit le manque au prorata de ce que chacun réclame :
            les listes gardaient 300 px chacune, le champ tombait à 75 px —
            « presque inutilisable », signalé le 9 octobre 2026.
          */}
          <div className="space-y-3">
            <form
              onSubmit={(e) => { e.preventDefault(); lancerRecherche(saisie); }}
              className="flex gap-2"
            >
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  ref={champDeRecherche}
                  type="text"
                  enterKeyHint="search"
                  aria-label="Chercher une facture"
                  placeholder="Numéro, nom, courriel ou communication structurée…"
                  className="form-input py-2 pl-10 pr-10"
                  value={saisie}
                  onChange={(e) => setSaisie(e.target.value)}
                />
                {(saisie !== '' || recherche !== '') && (
                  <button
                    type="button"
                    onClick={effacerRecherche}
                    title="Effacer la recherche"
                    aria-label="Effacer la recherche"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button type="submit" className="btn-outline shrink-0">Chercher</button>
            </form>

            {/*
              Dire ce que la règle a compris. Un écran qui cherche sans
              l'annoncer laisse deviner pourquoi il ne trouve pas — et c'est
              par la communication structurée qu'arrive un virement, donc
              c'est elle qu'on tape le plus souvent sans savoir sous quelle
              forme elle est stockée.
            */}
            {comprise.explication && (
              <p className={clsx(
                'text-sm',
                comprise.cleValide === false ? 'text-amber-800' : 'text-gray-500',
              )}>
                {comprise.explication}
              </p>
            )}

            {/* `sm:w-auto` défait le `w-full` de `form-input` : chaque liste
                prend la largeur de sa plus longue option, pas toute la ligne. */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <select
                aria-label="Filtrer les factures"
                className="form-input py-2 sm:w-auto"
                value={filtre}
                onChange={(e) => changerFiltre(e.target.value as Filtre)}
              >
                {FILTRES.map((f) => (
                  <option key={f.valeur} value={f.valeur}>{f.label}</option>
                ))}
              </select>
              <select
                aria-label="Trier les factures"
                className="form-input py-2 sm:w-auto"
                value={tri}
                onChange={(e) => changerTri(e.target.value as Tri)}
              >
                {(Object.keys(TRIS) as Tri[]).map((t) => (
                  <option key={t} value={t}>{TRIS[t].label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* --- Ce que la liste montre, et ce qu'elle ne montre pas --- */}
          {!chargement && !erreur && (
            <p className="text-sm text-gray-600">
              {total === 0 ? 'Aucune facture ne correspond.' : (
                <>
                  <strong className="tabular-nums">{total?.toLocaleString('fr-BE')}</strong>{' '}
                  facture{(total ?? 0) > 1 ? 's' : ''} correspond{(total ?? 0) > 1 ? 'ent' : ''} au
                  filtre. {lignes.length} affichée{lignes.length > 1 ? 's' : ''} ici, totalisant{' '}
                  <strong className="tabular-nums">{euros(centimesAffiches)}</strong> de reste dû.
                </>
              )}
            </p>
          )}

          {chargement ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : erreur ? (
            <div className="rounded-lg bg-red-50 p-4">
              <p className="flex items-start gap-2 text-red-700">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> {erreur}
              </p>
            </div>
          ) : lignes.length === 0 ? (
            <div className="rounded-xl bg-white p-8 text-center shadow-md">
              <p className="text-gray-600">
                Aucune facture ne correspond à ces critères.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl bg-white shadow-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3" />
                      <th className="px-4 py-3">Numéro</th>
                      <th className="px-4 py-3">Famille</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">État</th>
                      <th className="px-4 py-3 text-right">Net</th>
                      <th className="px-4 py-3 text-right">Reçu</th>
                      <th className="px-4 py-3 text-right">Reste dû</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {lignes.map((l) => {
                      const etat = ETATS[l.settlement];
                      const sesNotes = notes.get(l.invoiceId) ?? [];
                      const ouvert = deplie === l.invoiceId;
                      /*
                        Ce que le solde résiduel veut DIRE. Un seul booléen
                        « contradiction » confondait les dix factures de juin
                        2025 — document parti, note de crédit à émettre — avec
                        le panier abandonné qui ne réclame rien à personne.
                        L'écran annonçait « réclame encore » sur les deux.
                      */
                      const quoi = reclamation(l);
                      const marque = quoi === 'note_a_emettre' || quoi === 'annulation_seule'
                        ? RECLAMATIONS[quoi]
                        : null;
                      const empechement = empechementNoteDeCredit(l);
                      return (
                        /*
                          `Fragment` nommé, et non `<>` : c'est le fragment qui
                          est l'élément du tableau, donc c'est LUI qui porte la
                          clé. Sur `<tr>` seulement, React avertit à chaque
                          rendu et perd l'état du détail déplié au tri.
                        */
                        <Fragment key={l.invoiceId}>
                          <tr
                            className={clsx(
                              'hover:bg-gray-50',
                              quoi === 'note_a_emettre' && 'border-l-4 border-amber-500',
                              ouvert && 'bg-gray-50',
                            )}
                          >
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => setDeplie(ouvert ? null : l.invoiceId)}
                                title={ouvert ? 'Replier le détail' : 'Voir paiements et notes de crédit'}
                                aria-expanded={ouvert}
                                className="text-gray-400 hover:text-gray-700"
                              >
                                {ouvert
                                  ? <ChevronDown className="h-5 w-5" />
                                  : <ChevronRight className="h-5 w-5" />}
                              </button>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="font-mono text-xs font-medium text-gray-900">
                                {l.invoiceNumber}
                              </div>
                              {sesNotes.length > 0 && (
                                <div className="mt-0.5 text-xs text-indigo-700">
                                  {sesNotes.length} note{sesNotes.length > 1 ? 's' : ''} de crédit,{' '}
                                  {euros(l.notesCents)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900">
                                {etiquetteFamille(l.nom, l.prenom)}
                              </div>
                              {l.courriel && (
                                <div className="text-xs text-gray-500">{l.courriel}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {dateCourte(l.creeLe)}
                              {l.echeanceLe && (
                                <div className="text-xs text-gray-500">
                                  échéance {dateCourte(l.echeanceLe)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={clsx(
                                'inline-flex rounded-full px-2 py-1 text-xs font-semibold leading-5',
                                etat.classe,
                              )}>
                                {etat.label}
                              </span>
                              {marque && (
                                <div className={clsx('mt-1 text-xs font-medium', marque.classe)}>
                                  {marque.marque}
                                </div>
                              )}
                              {l.tropPercuCents > 0 && (
                                <div className="mt-1 text-xs font-medium text-amber-800">
                                  trop-perçu {euros(l.tropPercuCents)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900">
                              {euros(l.netCents)}
                              {l.notesCents > 0 && (
                                <div className="text-xs text-gray-500">
                                  sur {euros(l.factureCents)}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900">
                              {euros(l.recuCents)}
                            </td>
                            {/*
                              « Reste dû » = ce que la FAMILLE doit encore.
                              Sur une facture annulée, c'est zéro : la décision
                              d'annuler l'a éteinte. Le solde résiduel reste
                              affiché, mais en gris et nommé pour ce qu'il est
                              — une trace comptable, pas une créance. On ne
                              l'efface pas (§5.6), on cesse de l'appeler une
                              dette.
                            */}
                            <td className={clsx(
                              'px-4 py-3 text-right text-sm tabular-nums',
                              quoi === 'due' ? 'font-semibold text-gray-900' : 'text-gray-400',
                            )}>
                              {quoi === 'due' ? euros(l.resteCents) : '—'}
                              {(quoi === 'note_a_emettre' || quoi === 'annulation_seule') && (
                                <div className="text-xs font-normal text-gray-500">
                                  {euros(l.resteCents)} inscrits
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                {l.pdfUrl && (
                                  <a
                                    href={l.pdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Ouvrir le PDF de la facture"
                                    className="text-primary-600 hover:text-primary-900"
                                  >
                                    <FileText className="h-5 w-5" />
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => envoyerLaFacture(l)}
                                  disabled={envoiEnCours === l.invoiceNumber}
                                  title={l.pdfUrl
                                    ? 'Renvoyer la facture par courriel'
                                    : 'Produire la facture et l’envoyer'}
                                  className={clsx(
                                    'text-primary-600 hover:text-primary-900',
                                    envoiEnCours === l.invoiceNumber && 'opacity-40',
                                  )}
                                >
                                  {envoiEnCours === l.invoiceNumber
                                    ? <Loader2 className="h-5 w-5 animate-spin" />
                                    : <Send className="h-5 w-5" />}
                                </button>
                                {/*
                                  Émettre une note de crédit.

                                  `disabled` pour de vrai. La version du
                                  10 septembre le peignait en gris SANS le
                                  désactiver : il avait l'air interdit, ne
                                  disait pas pourquoi, et menait à une
                                  exception SQL brute si on insistait. C'est
                                  le pire des deux — signalé en production sur
                                  `CDV-TEST-2602043`.

                                  Le motif réel vient de la base :
                                  `create_credit_note` refuse toute facture
                                  déjà annulée. Le titre le nomme.
                                */}
                                <button
                                  type="button"
                                  onClick={() => setInvoiceACrediter(l.invoiceId)}
                                  disabled={empechement !== null}
                                  title={empechement ?? 'Émettre une note de crédit'}
                                  className={clsx(
                                    empechement
                                      ? 'cursor-not-allowed text-gray-300'
                                      : 'text-indigo-600 hover:text-indigo-900',
                                  )}
                                >
                                  <Receipt className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {ouvert && (
                            <tr>
                              <td colSpan={9} className="p-0">
                                <DetailFacture facture={l} onChange={charger} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* --- Pagination -------------------------------------- */}
              {pages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium tabular-nums">{page + 1}</span> sur{' '}
                    <span className="font-medium tabular-nums">{pages}</span>
                  </p>
                  <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => { setPage((p) => Math.max(0, p - 1)); setDeplie(null); }}
                      disabled={page === 0}
                      className={clsx(
                        'inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page === 0 ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50',
                      )}
                    >
                      <span className="sr-only">Précédent</span>
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPage((p) => Math.min(pages - 1, p + 1)); setDeplie(null); }}
                      disabled={page + 1 >= pages}
                      className={clsx(
                        'inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium',
                        page + 1 >= pages ? 'cursor-not-allowed text-gray-300' : 'text-gray-500 hover:bg-gray-50',
                      )}
                    >
                      <span className="sr-only">Suivant</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/*
        La modale d'émission, réutilisée telle quelle. Elle remet son état à
        zéro à chaque ouverture, calcule le montant par `planCreditNote` et le
        laisse replafonner par la base — trois décisions d'argent qu'elle prend
        explicitement, et qu'un clic sur une ligne de tableau prenait avant.
      */}
      <CreditNoteModal
        invoiceId={invoiceACrediter}
        onClose={() => setInvoiceACrediter(null)}
        onEmitted={charger}
      />
    </div>
  );
}

/** Un onglet, et rien de plus. */
function Onglets<T extends string>({
  courant, valeur, onChange, icone: Icone, children,
}: {
  courant: T;
  valeur: T;
  onChange: (v: T) => void;
  icone: typeof Receipt;
  children: React.ReactNode;
}) {
  const actif = courant === valeur;
  return (
    <button
      type="button"
      onClick={() => onChange(valeur)}
      aria-current={actif ? 'page' : undefined}
      className={clsx(
        '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium',
        actif
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
      )}
    >
      <Icone className="h-4 w-4" />
      {children}
    </button>
  );
}
